import {
  Box,
  Flex,
  Text,
  Float,
  Image,
  Stack,
  HStack,
  Circle,
  IconButton,
  useDisclosure,
  Spinner,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RightIcon, NotificationIcon } from '@/_assets/icon-components';
import { useRouter } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import Points from '@/_components/Points';
import { useQuery } from '@tanstack/react-query';
import Auth from '@/_components/Auth';
import { IOrderType, IRedux } from '@/_store/types';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import { onOpenMap } from '@/_store/common/common.slice';
import notificationService from '@/_service/notification.service';
import AutoGeolocate from '@/_components/AutoGeolocate';
import { useTelegram } from '@/_providers/telegram';
import SearchImg from '@/_assets/icons/search_mika.svg';
import { Link } from '@/i18n/navigation';

const HeaderMobile = () => {
  const [isOpen, setOpenModal] = useState(false);

  const { webApp } = useTelegram();
  const { open: isAuth, onClose, onOpen } = useDisclosure();

  const router = useRouter();
  const t = useTranslations();
  const dispatch = useDispatch();

  const { settings, points, user, permissions, order_type } = useSelector(
    (state: IRedux) => ({
      settings: state.settings.data,
      points: state.common.points,
      user: state.auth.user,
      permissions: state.session.permissions,
      order_type: state.session.order_type,
    }),
    shallowEqual,
  );

  const activePoint = useMemo(
    () => points.find((point) => point.isActive),
    [points],
  );

  const { data: notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: () =>
      notificationService
        .getAll({
          page: 1,
          limit: 20,
          only_unread: true,
        })
        .then((res) => res.data),
    enabled: Boolean(user?.id),
  });

  const handleOpenMap = (type: IOrderType['type'] | null) => {
    if (type === 'delivery' && points?.length > 0) {
      return setOpenModal(true);
    }

    dispatch(onOpenMap());
  };

  const handleNotifications = () => {
    if (user?.id) {
      router.push('/notifications');
    } else {
      onOpen();
    }
  };

  return (
    <>
      <AutoGeolocate />
      <Box px={4} pt={webApp?.isFullscreen ? 6 : 0}>
        <HStack justifyContent={'space-between'}>
          {!settings?.shipper_id ? (
            <HStack gap={4} flex={1}>
              {/* <Skeleton height='48px' width='48px' rounded={12} /> */}
              <Stack gap={1} style={{ flex: 1 }}>
                <SkeletonText noOfLines={1} width='150px' />
                <SkeletonText noOfLines={1} width='100px' />
              </Stack>
            </HStack>
          ) : (
            <HStack gap={4} flex={1}>
              <Flex flexDir={'column'}>
                <Flex
                  alignItems={'center'}
                  gap={'6px'}
                  fontSize={'sm'}
                  color='text'
                  fontWeight={600}
                  onClick={() => handleOpenMap('delivery')}
                >
                  <Text maxW={'162px'} truncate>
                    {activePoint?.address &&
                    permissions.geolocation === 'accepted' ? (
                      activePoint?.address
                    ) : permissions.geolocation === 'pending' ? (
                      <>
                        {t('location.determining_location')}
                        <Spinner size='xs' ml={1} />
                      </>
                    ) : (
                      t('add_address')
                    )}
                  </Text>
                  <RightIcon />
                </Flex>
                <Text color='#8E8E8E' fontSize={14}>
                  {order_type?.type === 'delivery'
                    ? t('delivery_address')
                    : t('from_the_branch')}
                </Text>
              </Flex>
            </HStack>
          )}
          <HStack>
            <Box position='relative' w='100%' h='100%'>
              <Box
                cursor={'pointer'}
                onClick={handleNotifications}
                rounded={'full'}
                color='#2D264B'
              >
                <NotificationIcon />
              </Box>
              {Number(notifications?.count || 0) > 0 && user?.id && (
                <Float>
                  <Circle
                    fontSize={'xs'}
                    size='5'
                    bg='primary'
                    color='primary.text'
                  >
                    {notifications?.count}
                  </Circle>
                </Float>
              )}
            </Box>
          </HStack>
        </HStack>
        <IconButton
          mt={'9px'}
          variant={'subtle'}
          rounded={16}
          w={'full'}
          size={'lg'}
          color='dark.900'
          asChild
        >
          <Link href='/mobile-search'>
            <Image src={SearchImg.src} alt='search' />
            {t('search')}
          </Link>
        </IconButton>
      </Box>
      <Auth
        open={isAuth}
        onClose={onClose}
        onLastConfirm={() => router.push('/profile')}
      />
      <Points
        isOpen={isOpen && points?.length > 0}
        onClose={() => setOpenModal(false)}
      />
    </>
  );
};

export default HeaderMobile;
